import React, { useState, useEffect } from "react";
import axios from "axios"; // Make sure to install axios: npm install axios
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  BanknotesIcon,
  Cog6ToothIcon,
  TagIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  UserIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import { authenticatedAxios } from "../apis";
import useSubscriptionList from "../stores/subscriptions/subscriptions";

// ===================================================================================
// ==  COMPONENT 1: User Subscriptions List (UPDATED)
// ===================================================================================

const UserSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPackage, setSelectedPackage] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const {
    subscriptionUsers,
    isLoadingUsers,
    plans,
    actions: { getSubscriptionUsers, getPlans },
  } = useSubscriptionList();

  useEffect(() => {
    getPlans(); // Fetch plans for the filter dropdown
  }, [getPlans]);

  useEffect(() => {
    // Debounce search input
    const debounceTimer = setTimeout(() => {
      getSubscriptionUsers({
        page: currentPage,
        size: 10,
        productId: selectedPackage || undefined,
        // search: searchTerm || undefined, // Add search to API call when backend supports it
      });
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [currentPage, selectedPackage, searchTerm, getSubscriptionUsers]);

  // Mock data for subscriptions, assuming all data comes from the backend

  // Simplified useEffect to simulate fetching data directly
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const filteredSubscriptions = (subscriptionUsers.results || []).filter(
    (user) => {
      const searchLower = searchTerm.toLowerCase();
      const name = user.name || user.username || "";
      return (
        name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower)
      );
    },
  );

  const packageColors = {
    "Annually Pro": "bg-purple-100 text-purple-800",
    "Monthly Pro": "bg-indigo-100 text-indigo-800",
    "Annually Standard": "bg-blue-100 text-blue-800",
    "Monthly Standard": "bg-cyan-100 text-cyan-800",
    "Free Trial": "bg-gray-100 text-gray-800",
  };

  const getEmptyMessage = () => {
    if (selectedPackage && selectedPackage !== "") {
      const selectedPlan = (plans || []).find(
        (p) => p.product_id === selectedPackage,
      );
      const packageName = selectedPlan
        ? selectedPlan.product_name
        : "the selected package";
      return `No user subscription found for package ${packageName}`;
    }
    return "No users found matching your criteria.";
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-grow max-w-md">
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search by user name, email or UID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div>
            <select
              className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={selectedPackage}
              onChange={(e) => setSelectedPackage(e.target.value)}
            >
              {/* <option value="">All Packages</option> */}
              {/* [PERBAIKAN] Menambahkan pengaman (plans || []) */}
              {(plans || []).map((plan) => (
                <option key={plan.product_id} value={plan.product_id}>
                  {plan.product_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-100">
        <div className="overflow-x-auto">
          {isLoadingUsers ? (
            <div className="flex justify-center items-center py-20">
              <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
              <span className="ml-3 text-gray-500">
                Loading subscriptions...
              </span>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    User
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Package Details
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Subscribed Since
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Subscription Until
                  </th>
                  {/* <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Total Duration (Months)
                  </th> */}
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Total Revenue
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSubscriptions.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-[100px] whitespace-nowrap text-center text-sm text-gray-500"
                    >
                      {getEmptyMessage()}
                    </td>
                  </tr>
                ) : (
                  filteredSubscriptions.map((subscription) => (
                    <tr key={subscription.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <div className="text-sm font-medium text-gray-900">
                          {subscription.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {subscription.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <span
                          className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                            packageColors[
                              subscription.subscription.product_name
                            ]
                          }`}
                        >
                          {subscription.subscription.product_name}
                        </span>
                        <div className="text-sm text-gray-500 mt-1">
                          {subscription.subscription.product_description}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                        {formatDate(subscription.subscription.start)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                        {formatDate(subscription.subscription.end)}
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-left">
                      {subscription.totalDurationMonths}
                    </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-left">
                        {subscription.subscription.price_amount}{" "}
                        {subscription.subscription.price_currency}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-left">
                        <span
                          className={`px-2.5 py-1 text-xs font-semibold rounded-full capitalize ${
                            subscription.is_active === true
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {subscription.is_active === true
                            ? "Active"
                            : "Not Active"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={!subscriptionUsers.has_next}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">
                {((subscriptionUsers.page || 1) - 1) *
                  (subscriptionUsers.size || 10) +
                  1}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(
                  (subscriptionUsers.page || 1) *
                    (subscriptionUsers.size || 10),
                  subscriptionUsers.total,
                )}
              </span>{" "}
              of <span className="font-medium">{subscriptionUsers.total}</span>{" "}
              results
            </p>
          </div>
          <div>
            <nav
              className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
              aria-label="Pagination"
            >
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={!subscriptionUsers.has_next}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================================================================================
// == COMPONENT 2: Plan Management Panel (INTEGRATED WITH API)
// ===================================================================================

const PlanManagement = () => {
  const emptyPlan = {
    id: null, // Will be product_id from Stripe
    name: "",
    description: "",
    monthlyPrice: "",
    annuallyPrice: "",
    monthlyPriceId: null,
    annuallyPriceId: null,
    features: [""], // Manual/additional features
    badge_text: "",
    botRestrictions: { dca: 0, grid: 0, rebalance: 0, v2: 0, v3: 0, lev: 0 },
  };

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [currentPlan, setCurrentPlan] = useState(emptyPlan);

  // Function to fetch and process data from the API
  const fetchPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      // TODO: Ensure authentication headers (JWT) are added, possibly via an axios interceptor
      const response = await authenticatedAxios.get(
        `/api/admin/subscription/plans`,
      ); // <-- FIXED: Used full URL
      const processedPlans = processApiData(response.data);
      setPlans(processedPlans);
    } catch (err) {
      console.error("Error fetching plans:", err);
      setError("Failed to load plans. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Process raw data from the API into a structure usable by the UI
  const processApiData = (apiData) => {
    const groupedPlans = {};

    apiData.forEach((item) => {
      const productId = item.product_id;
      if (!groupedPlans[productId]) {
        let manualFeatures = [""];
        const featuresJson = item.product_metadata?.additional_features;
        if (featuresJson) {
          try {
            const parsedFeatures = JSON.parse(featuresJson);
            if (Array.isArray(parsedFeatures) && parsedFeatures.length > 0) {
              manualFeatures = parsedFeatures;
            }
          } catch (e) {
            console.error("Failed to parse additional_features JSON:", e);
          }
        }

        groupedPlans[productId] = {
          id: productId,
          name: item.product_name,
          description: item.description || "",
          monthlyPrice: "",
          annuallyPrice: "",
          monthlyPriceId: null,
          annuallyPriceId: null,
          badge_text: item.product_metadata?.badge_text || "",
          // Extract bot limits from product metadata
          botRestrictions: {
            dca: parseInt(item.product_metadata?.dca_bot_limit || 0, 10),
            grid: parseInt(item.product_metadata?.grid_bot_limit || 0, 10),
            rebalance: parseInt(
              item.product_metadata?.rebalance_bot_limit || 0,
              10,
            ),
            v2: parseInt(item.product_metadata?.V2_bot_limit || 0, 10),
            v3: parseInt(item.product_metadata?.V3_bot_limit || 0, 10),
            lev: parseInt(item.product_metadata?.lev_bot_limit || 0, 10),
          },
          features: manualFeatures,
        };
      }

      const plan = groupedPlans[productId];
      // Price from Stripe is in cents, convert to main currency unit
      const priceInMainUnit = (item.unit_amount / 100).toString();

      if (item.interval === "month") {
        plan.monthlyPrice = priceInMainUnit;
        plan.monthlyPriceId = item.price_id;
      } else if (item.interval === "year") {
        plan.annuallyPrice = priceInMainUnit;
        plan.annuallyPriceId = item.price_id;
      }
    });

    return Object.values(groupedPlans);
  };

  const openModal = (mode, plan = null) => {
    setModalMode(mode);
    if (mode === "create") {
      setCurrentPlan(emptyPlan);
    } else {
      setCurrentPlan({ ...plan });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentPlan(emptyPlan);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setCurrentPlan((prev) => ({ ...prev, [name]: value }));
  };

  const handleBotLimitChange = (e) => {
    const { name, value } = e.target;
    const numValue = parseInt(value, 10) || 0;
    setCurrentPlan((prev) => ({
      ...prev,
      botRestrictions: {
        ...prev.botRestrictions,
        [name]: Math.max(0, numValue),
      },
    }));
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...currentPlan.features];
    newFeatures[index] = value;
    setCurrentPlan((prev) => ({ ...prev, features: newFeatures }));
  };

  const addFeature = () => {
    setCurrentPlan((prev) => ({ ...prev, features: [...prev.features, ""] }));
  };

  const removeFeature = (index) => {
    const newFeatures = currentPlan.features.filter((_, i) => i !== index);
    setCurrentPlan((prev) => ({
      ...prev,
      features: newFeatures.length > 0 ? newFeatures : [""],
    }));
  };

  const handleSavePlan = async () => {
    if (!currentPlan.name) {
      alert("Plan name is required.");
      return;
    }

    if (modalMode === "edit") {
      const payload = {
        product_id: currentPlan.id,
        product_name: currentPlan.name,
        description: currentPlan.description,
        // Bot limits
        dca_bot_limit: currentPlan.botRestrictions.dca,
        grid_bot_limit: currentPlan.botRestrictions.grid,
        rebalance_bot_limit: currentPlan.botRestrictions.rebalance,
        V2_bot_limit: currentPlan.botRestrictions.v2,
        V3_bot_limit: currentPlan.botRestrictions.v3,
        lev_bot_limit: currentPlan.botRestrictions.lev,
        // New fields for metadata
        badge_text: currentPlan.badge_text,
        additional_features: JSON.stringify(
          currentPlan.features.filter((f) => f.trim() !== ""),
        ),
      };

      try {
        // TODO: Ensure authentication headers (JWT) are added
        await authenticatedAxios.put(`/api/admin/subscription/update`, payload); // <-- FIXED: Used full URL
        // After success, refetch data to ensure UI is in sync
        await fetchPlans();
        alert("Plan updated successfully!");
        closeModal();
      } catch (err) {
        console.error("Error updating plan:", err);
        alert(
          `Failed to update plan: ${err.response?.data?.error || err.message}`,
        );
      }
    }
  };

  const getBotFeaturesForDisplay = (restrictions) => {
    return Object.entries(restrictions)
      .filter(([, value]) => value > 0)
      .map(([key, value]) => `${value} ${key.toUpperCase()} Bots`);
  };

  // Combine features from description, bot limits, and manual features for display
  const getDisplayFeatures = (plan) => {
    const features = [];
    // if (plan.description) {
    //   features.push(plan.description);
    // }
    features.push(...getBotFeaturesForDisplay(plan.botRestrictions));
    if (plan.features && plan.features.length > 0) {
      features.push(...plan.features.filter((f) => f.trim() !== ""));
    }
    return features;
  };

  if (loading)
    return (
      <div className="flex justify-center items-center py-20">
        <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
        <span className="ml-3 text-gray-500">Loading plans...</span>
      </div>
    );
  if (error) return <p className="text-center text-red-500">{error}</p>;

  return (
    <>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-5">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="bg-white border rounded-lg shadow-sm flex flex-col relative"
            >
              {plan.badge_text && (
                <div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 bg-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  {plan.badge_text}
                </div>
              )}
              <div className="p-6">
                <h3 className="text-xl font-bold text-center text-gray-800">
                  {plan.name}
                </h3>
                <p className="text-center text-gray-500 text-sm h-6">
                  {plan.monthlyPrice && `$${plan.monthlyPrice}/month`}
                  {plan.monthlyPrice && plan.annuallyPrice && ` • `}
                  {plan.annuallyPrice && `$${plan.annuallyPrice}/year`}
                </p>
                <hr className="my-4" />
                <p className="text-sm text-gray-600 text-center mb-4">
                  {plan.description}
                </p>
                <ul className="space-y-2 text-sm text-gray-600 min-h-[120px]">
                  {getDisplayFeatures(plan).map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <span className="text-green-500 mr-2 mt-1 flex-shrink-0">
                        &#10003;
                      </span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-auto p-4 bg-gray-50 border-t flex justify-end space-x-2">
                <button
                  onClick={() => openModal("edit", plan)}
                  className="text-sm text-blue-600 hover:text-blue-800 p-2"
                  title="Edit Plan"
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-semibold">
                {modalMode === "create" ? "Create New Plan" : "Edit Plan"}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-900"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column: Basic Info */}
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium">
                      Plan Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={currentPlan.name}
                      onChange={handleFormChange}
                      className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5"
                      placeholder="e.g., Ultimate"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={currentPlan.description}
                      onChange={handleFormChange}
                      className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5"
                      rows="3"
                      placeholder="Brief plan description"
                    ></textarea>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-2 text-sm font-medium">
                        Monthly Price ($)
                      </label>
                      <input
                        type="number"
                        name="monthlyPrice"
                        value={currentPlan.monthlyPrice}
                        onChange={handleFormChange}
                        className="bg-gray-200 border border-gray-300 text-sm rounded-lg block w-full p-2.5 cursor-not-allowed"
                        placeholder="e.g., 25"
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium">
                        Annually Price ($)
                      </label>
                      <input
                        type="number"
                        name="annuallyPrice"
                        value={currentPlan.annuallyPrice}
                        onChange={handleFormChange}
                        className="bg-gray-200 border border-gray-300 text-sm rounded-lg block w-full p-2.5 cursor-not-allowed"
                        placeholder="e.g., 250"
                        readOnly
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Prices cannot be changed from this panel. Please modify them
                    directly in the Stripe Dashboard.
                  </p>
                  <div>
                    <label className="block mb-2 text-sm font-medium">
                      Badge Text (Optional)
                    </label>
                    <input
                      type="text"
                      name="badge_text"
                      value={currentPlan.badge_text}
                      onChange={handleFormChange}
                      className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5"
                      placeholder="e.g., Best Deal"
                    />
                  </div>
                </div>

                {/* Right Column: Features & Bot Config */}
                <div className="space-y-6">
                  <div>
                    <h4 className="text-md font-semibold text-gray-800 mb-2">
                      Bot Limit Configuration
                    </h4>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.keys(currentPlan.botRestrictions).map((key) => (
                        <div key={key}>
                          <label className="block mb-2 text-sm font-medium capitalize">
                            {key} Bots
                          </label>
                          <input
                            type="number"
                            name={key}
                            min="0"
                            value={currentPlan.botRestrictions[key]}
                            onChange={handleBotLimitChange}
                            className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium">
                      Additional Features
                    </label>
                    <div className="space-y-2">
                      {currentPlan.features.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2"
                        >
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) =>
                              handleFeatureChange(index, e.target.value)
                            }
                            className="bg-gray-50 border border-gray-300 text-sm rounded-lg block w-full p-2.5"
                            placeholder="e.g., Priority Support"
                          />
                          <button
                            onClick={() => removeFeature(index)}
                            className="text-red-500 p-1 flex-shrink-0"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={addFeature}
                      className="text-sm text-blue-600 mt-2 hover:underline"
                    >
                      + Add Feature
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end p-6 border-t space-x-2 mt-auto">
              <button
                onClick={closeModal}
                className="text-gray-500 bg-white hover:bg-gray-100 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePlan}
                className="text-white bg-blue-700 hover:bg-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
              >
                Save Plan
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ===================================================================================
// == PARENT COMPONENT: Main Page with Tab Navigation
// ===================================================================================

export default function SubscriptionsPage() {
  const [activeTab, setActiveTab] = useState("subscriptions"); // 'subscriptions' or 'plans'

  const TabButton = ({ name, label, activeTab, setActiveTab }) => (
    <button
      onClick={() => setActiveTab(name)}
      className={`px-4 py-3 text-sm font-medium transition-colors ${
        activeTab === name
          ? "border-b-2 border-blue-600 text-blue-600"
          : "border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="space-y-6">
          {/* Page Header */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Subscriptions</h2>
            <p className="mt-1 text-sm text-gray-500">
              {activeTab === "subscriptions"
                ? "View all active and past user subscriptions."
                : "Create, edit, and manage the subscription plans available to users."}
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-6" aria-label="Tabs">
              <TabButton
                name="subscriptions"
                label="User Subscriptions"
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />
              <TabButton
                name="plans"
                label="Plan Management"
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />
            </nav>
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {activeTab === "subscriptions" && <UserSubscriptions />}
            {activeTab === "plans" && <PlanManagement />}
          </div>
        </div>
      </div>
    </div>
  );
}
